import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, ExternalLink, Sparkles, ArrowRight } from 'lucide-react';

import { cn } from '../lib/cn';

export const Route = createFileRoute('/podglad/$slug')({
  validateSearch: (s) =>
    ({ token: typeof s.token === 'string' ? s.token : undefined }) as { token?: string },
  component: PreviewPage,
});

const API = import.meta.env.VITE_API_BASE_URL ?? '';

function PreviewPage() {
  const { slug } = Route.useParams();
  const previewBase =
    typeof window !== 'undefined' && window.location.hostname.endsWith('localhost')
      ? `http://${slug}.localhost:8787`
      : `https://${slug}.pozyskajpacjenta.pl`;

  const [interval, setInterval] = React.useState<'month' | 'year'>('month');
  const activate = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API}/api/public/onboarding/activate/${slug}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ interval }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'activate_failed');
      }
      return (await res.json()) as { checkout_url: string };
    },
    onSuccess: (r) => {
      if (r.checkout_url) window.location.href = r.checkout_url;
    },
  });

  return (
    <>
      {/* Activation bar — sticky at top of viewport */}
      <div className="sticky top-0 z-50 border-b border-accent/30 bg-accent text-bg shadow-md">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-6 py-3">
          <div className="flex items-center gap-2 text-sm">
            <Sparkles className="size-4 shrink-0" aria-hidden="true" />
            <span>
              Twoja strona jest gotowa pod{' '}
              <a
                href={previewBase}
                target="_blank"
                rel="noreferrer"
                className="font-mono underline underline-offset-2"
              >
                {slug}.pozyskajpacjenta.pl
              </a>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 rounded-full bg-bg/20 p-1 text-xs">
              {(['month', 'year'] as const).map((i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => setInterval(i)}
                  className={cn(
                    'rounded-full px-3 py-1 font-medium transition',
                    interval === i ? 'bg-bg text-text' : 'text-bg/80 hover:text-bg'
                  )}
                >
                  {i === 'month' ? '99 zł/mies.' : '999 zł/rok'}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => activate.mutate()}
              disabled={activate.isPending}
              className="inline-flex items-center gap-1.5 rounded-full bg-text px-4 py-2 text-sm font-semibold text-bg hover:bg-text/90 disabled:opacity-60"
            >
              {activate.isPending ? 'Przekierowuję…' : 'Aktywuj stronę'}
              <ArrowRight className="size-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Iframe preview */}
      <section className="px-4 py-6 md:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h1 className="font-display text-2xl font-semibold tracking-tight text-text md:text-3xl">
              Podgląd Twojej strony
            </h1>
            <a
              href={previewBase}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-accent hover:underline"
            >
              Otwórz w pełnym oknie <ExternalLink className="size-3.5" aria-hidden="true" />
            </a>
          </div>

          <div className="mt-4 overflow-hidden rounded-2xl bg-bg ring-1 ring-border shadow-lg">
            <iframe
              src={previewBase}
              title={`Podgląd ${slug}`}
              className="h-[80vh] w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* Activation FAQ */}
      <section className="px-6 py-16 bg-surface/60 border-t border-border">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-center font-display text-2xl font-semibold tracking-tight text-text md:text-3xl">
            Co dostajesz po aktywacji
          </h2>
          <ul className="mt-8 space-y-3 text-sm">
            {INCLUDED.map((it) => (
              <li key={it} className="flex items-start gap-3 rounded-md bg-bg p-3 ring-1 ring-border">
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" aria-hidden="true" />
                <span className="text-text">{it}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 rounded-2xl bg-accent-soft p-5 ring-1 ring-accent/20">
            <p className="font-display text-base font-semibold text-accent">
              Promocja startowa
            </p>
            <p className="mt-1 text-sm text-text">
              Nielimitowane edycje strony w każdym miesiącu — do czasu, aż prześcigniemy
              ZnanyLekarz i Booksy. Po promocji przywracamy normalne limity, ale wszystkie
              edycje wykonane w okresie promocyjnym zostają.
            </p>
          </div>

          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => activate.mutate()}
              disabled={activate.isPending}
              className="inline-flex items-center gap-2 rounded-full bg-text px-6 py-3 text-base font-medium text-bg hover:bg-text/90"
            >
              Aktywuj — {interval === 'month' ? '99 zł/mies.' : '999 zł/rok'}
              <ArrowRight className="size-4" aria-hidden="true" />
            </button>
            {activate.error ? (
              <p className="mx-auto mt-3 max-w-md rounded-md bg-error/10 p-3 text-sm text-error ring-1 ring-error/20">
                Coś nie zadziałało: {(activate.error as Error).message}
              </p>
            ) : null}
            <p className="mt-3 text-xs text-text-subtle">
              Płatność przez Stripe (karty, BLIK, Przelewy24). Faktura VAT wystawiana automatycznie.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

const INCLUDED = [
  'Strona kliniki na własnej (lub naszej) domenie z SSL',
  'Kalendarz rezerwacji 24/7, bez prowizji od wizyt',
  'Multi-doktorski widok koordynacji + iCal eksport per lekarz',
  'SMS przypomnienia T-24h i drip kampanie (potwierdzenia, recall, podziękowania)',
  'Automatyczne prośby o opinię na Google po wizycie',
  'Lead form RODO-ready + automatycznie generowana polityka prywatności',
  'Panel admin: zespół, usługi, FAQ, opinie, media',
  'Integracja z Booksy / znanylekarz (osobno, opcjonalna)',
  'Cron-driven synchronizacja Google Business Profile',
  'Wsparcie techniczne mailem + okres promocyjny: nielimitowane edycje',
];
